// src/api/server/cashRegister.ts
import { axiosServerData, NetsuiteInstanceProps } from 'src/utils/axios';

// ----------------------------------------------------------------------

const param = { firstEndPoint: 'cashier' } as NetsuiteInstanceProps;

const initParam = ({
  idUser,
  data,
  endUrl,
  firstEndPoint,
  lastEndPoint,
  method,
  endpointParam,
  headers,
}: NetsuiteInstanceProps) => {
  param.data = data;
  param.endUrl = endUrl || '';
  param.idUser = idUser;
  param.firstEndPoint = firstEndPoint ?? 'cashier';
  param.lastEndPoint = lastEndPoint;
  // @ts-ignore
  param.endpointParam = endpointParam;
  param.method = method;
  param.headers = headers || {};
};

const buildCashRegisterHeaders = (cashRegisterId?: string) =>
  cashRegisterId ? { 'x-cash-register-id': cashRegisterId } : {};

const CURRENT_SESSION_CACHE_TTL_MS = 1500;
const currentSessionRequests = new Map<string, Promise<any>>();
const currentSessionCache = new Map<string, { expiresAt: number; response: any }>();

const getCurrentSessionRequestKey = (idUser: string, cashRegisterId?: string) =>
  `${idUser}:${cashRegisterId || 'default'}`;

const clearCurrentSessionCache = (idUser: string, cashRegisterId?: string) => {
  currentSessionCache.delete(getCurrentSessionRequestKey(idUser, cashRegisterId));
  if (cashRegisterId) {
    currentSessionCache.delete(getCurrentSessionRequestKey(idUser));
  }
};

// Opérations sur les caisses enregistreuses
export const getCashRegisters = async (idUser: string) => {
  initParam({
    idUser,
    firstEndPoint: 'user',
    lastEndPoint: 'cashRegister',
    method: 'get',
    endUrl: '/list',
  } as NetsuiteInstanceProps);
  return axiosServerData(param);
};

export const getCashRegister = async (idUser: string, cashRegisterId: string) => {
  initParam({
    idUser,
    lastEndPoint: 'cashRegister',
    method: 'get',
    endUrl: `/${cashRegisterId}`,
  } as NetsuiteInstanceProps);
  return axiosServerData(param);
};

export const createCashRegister = async (idUser: string, data: { name: string }) => {
  initParam({
    idUser,
    data,
    lastEndPoint: 'cashRegister',
    method: 'post',
  } as NetsuiteInstanceProps);
  return axiosServerData(param);
};

export const updateCashRegister = async (idUser: string, cashRegisterId: string, data: any) => {
  initParam({
    idUser,
    data,
    lastEndPoint: 'cashRegister',
    method: 'patch',
    endUrl: `/${cashRegisterId}`,
  } as NetsuiteInstanceProps);
  return axiosServerData(param);
};

// Gestion des sessions de caisse
export const getCurrentSession = async (idUser: string, cashRegisterId?: string) => {
  if (!idUser) {
    return {
      data: null,
      error: 'Missing user id',
    };
  }

  const requestKey = getCurrentSessionRequestKey(idUser, cashRegisterId);
  const cachedSession = currentSessionCache.get(requestKey);

  if (cachedSession && cachedSession.expiresAt > Date.now()) {
    return cachedSession.response;
  }

  const pendingRequest = currentSessionRequests.get(requestKey);
  if (pendingRequest) return pendingRequest;

  const request = (async () => {
    try {
      initParam({
        idUser,
        lastEndPoint: 'cashRegister',
        method: 'get',
        endUrl: `/current-session`,
        headers: buildCashRegisterHeaders(cashRegisterId),
      } as NetsuiteInstanceProps);
      const response = await axiosServerData(param);
      currentSessionCache.set(requestKey, {
        response,
        expiresAt: Date.now() + CURRENT_SESSION_CACHE_TTL_MS,
      });
      return response;
    } catch (error: any) {
      console.error('Error fetching current session:', error);
      // Retourner une structure de données vide en cas d'erreur
      const response = {
        data: null,
        error: error?.message || error || 'Failed to fetch current session',
      };
      currentSessionCache.set(requestKey, {
        response,
        expiresAt: Date.now() + CURRENT_SESSION_CACHE_TTL_MS,
      });
      return response;
    } finally {
      currentSessionRequests.delete(requestKey);
    }
  })();

  currentSessionRequests.set(requestKey, request);
  return request;
};

export const openSession = async (
  idUser: string,
  cashRegisterId: string | undefined,
  data: { openingAmount: number; openingDenominations?: any[]; notes?: string; userId?: string }
) => {
  console.log('[openSession API] Called with:', { idUser, cashRegisterId, data });
  initParam({
    idUser,
    data: { ...data, userId: data.userId || idUser },
    lastEndPoint: 'cashRegister',
    method: 'post',
    endUrl: `/open`,
  } as NetsuiteInstanceProps);
  const result = await axiosServerData(param);
  clearCurrentSessionCache(idUser, cashRegisterId);
  console.log('[openSession API] Result:', result);
  return result;
};

export const closeSession = async (
  idUser: string,
  cashRegisterId: string | undefined,
  data: { closingAmount: number; closingDenominations?: any[]; notes?: string; userId?: string }
) => {
  console.log('[closeSession API] Called with:', { idUser, cashRegisterId, data });
  initParam({
    idUser,
    data: { ...data, userId: data.userId || idUser },
    lastEndPoint: 'cashRegister',
    method: 'post',
    endUrl: `/close`,
  } as NetsuiteInstanceProps);
  const result = await axiosServerData(param);
  clearCurrentSessionCache(idUser, cashRegisterId);
  console.log('[closeSession API] Result:', result);
  return result;
};

// Obtenir la dernière session fermée (pour le montant d'ouverture par défaut)
export const getLastClosedSession = async (idUser: string) => {
  initParam({
    idUser: idUser, // Utiliser cashRegisterId si fourni, sinon idUser
    lastEndPoint: 'cashRegister',
    method: 'get',
    endUrl: `/last-closed-session`,
  } as NetsuiteInstanceProps);
  return axiosServerData(param);
};

// Gestion des transactions
export const getSessionTransactions = async (
  idUser: string,
  cashRegisterId: string | undefined,
  sessionId: string
) => {
  const paramCashier = { firstEndPoint: 'cashier' } as NetsuiteInstanceProps;
  const initParamCashier = ({
    data,
    endUrl,
    lastEndPoint,
    method,
    endpointParam,
  }: NetsuiteInstanceProps) => {
    paramCashier.data = data;
    paramCashier.endUrl = endUrl || '';
    paramCashier.idUser = idUser;
    paramCashier.lastEndPoint = lastEndPoint;
    // @ts-ignore
    paramCashier.endpointParam = endpointParam;
    paramCashier.method = method;
    paramCashier.headers = {};
  };

  initParamCashier({
    idUser,
    lastEndPoint: 'cashTransactions',
    method: 'get',
    endpointParam: `session/${sessionId}`,
  } as NetsuiteInstanceProps);
  return axiosServerData(paramCashier);
};

export const createTransaction = async (
  idUser: string,
  cashRegisterId: string | undefined,
  data: {
    cashRegister?: string;
    cashRegisterSession?: string;
    type: 'sale' | 'refund' | 'deposit' | 'withdrawal';
    amount: number;
    paymentMethod: string;
    reference?: string;
    notes?: string;
    items?: any[];
    payments?: any[];
  }
) => {
  const paramCashier = { firstEndPoint: 'cashier' } as NetsuiteInstanceProps;
  const initParamCashier = ({
    endUrl,
    lastEndPoint,
    method,
    endpointParam,
  }: NetsuiteInstanceProps) => {
    paramCashier.data = data;
    paramCashier.endUrl = endUrl || '';
    paramCashier.idUser = idUser;
    paramCashier.lastEndPoint = lastEndPoint;
    // @ts-ignore
    paramCashier.endpointParam = endpointParam;
    paramCashier.method = method;
  };

  initParamCashier({
    idUser,
    data,
    lastEndPoint: 'cashTransactions',
    method: 'post',
  } as NetsuiteInstanceProps);
  return axiosServerData(paramCashier);
};
